<template>
  <a-drawer
    :visible="visible"
    :title="`Invoice #${invoiceData && invoiceData.id ? invoiceData.id : ''} - Disbursements`"
    placement="right"
    width="800px"
    @close="handleClose"
    :closable="true"
  >
    <div v-if="invoiceData" style="padding: 0 16px">
      <!-- Invoice Information Section -->
      <div style="margin-bottom: 32px">
        <h3 style="margin-bottom: 16px; font-size: 16px; font-weight: 600; color: #05004E">
          Invoice Information
        </h3>
        <div style="background: #f9fafb; padding: 20px; border-radius: 8px">
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px">
            <div>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280; font-weight: 500">
                Invoice ID
              </p>
              <p style="margin: 0; font-size: 14px; font-weight: 600; color: #05004E">
                {{ invoiceData.id }}
              </p>
            </div>
            <div>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280; font-weight: 500">
                Buyer Name
              </p>
              <p style="margin: 0; font-size: 14px; font-weight: 600; color: #05004E">
                {{ invoiceData.customerBusinessName }}
              </p>
            </div>
            <div>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280; font-weight: 500">
                Seller Name
              </p>
              <p style="margin: 0; font-size: 14px; font-weight: 600; color: #05004E">
                {{ invoiceData.sellerName }}
              </p>
            </div>
            <div>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280; font-weight: 500">
                Amount Payable
              </p>
              <p style="margin: 0; font-size: 14px; font-weight: 600; color: #05004E">
                {{ formatAmount(invoiceData.amountPayable) }}
              </p>
            </div>
            <div>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280; font-weight: 500">
                Total Disbursed
              </p>
              <p style="margin: 0; font-size: 14px; font-weight: 600; color: #26a246">
                {{ formatAmount(invoiceData.totalDisbursed) }}
              </p>
            </div>
            <div>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280; font-weight: 500">
                Disbursement Status
              </p>
              <StatusTag :status="invoiceData.disbursementStatus" />
            </div>
          </div>
        </div>
      </div>

      <!-- Disbursement History Section -->
      <div style="margin-bottom: 32px">
        <h3 style="margin-bottom: 16px; font-size: 16px; font-weight: 600; color: #05004E">
          Disbursement History
        </h3>
        
        <div v-if="disbursementLoading" style="display: flex; justify-content: center; align-items: center; height: 200px">
          <a-spin size="large" />
        </div>
        
        <div v-else-if="disbursementHistory.length === 0" style="display: flex; justify-content: center; align-items: center; height: 200px; color: #6b7280">
          <div style="text-align: center">
            <a-icon type="inbox" style="font-size: 48px; margin-bottom: 16px; color: #d1d5db" />
            <p>No disbursements found for this invoice</p>
          </div>
        </div>
        
        <div v-else>
          <a-table
            :columns="disbursementColumns"
            :data-source="disbursementHistory"
            row-key="id"
            :pagination="false"
            :scroll="{ x: 'max-content' }"
            size="small"
          >
            <template slot="amount" slot-scope="text">
              {{ formatAmount(text) }}
            </template>
            <template slot="status" slot-scope="text">
              <StatusTag :status="text" />
            </template>
            <template slot="dateSent" slot-scope="text">
              {{ formatDate(text) }}
            </template>
            <template slot="dateCompleted" slot-scope="text">
              {{ text ? formatDate(text) : 'N/A' }}
            </template>
          </a-table>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-else-if="loading" style="display: flex; justify-content: center; align-items: center; height: 200px">
      <a-spin size="large" />
    </div>

    <!-- Error State -->
    <div v-else style="display: flex; justify-content: center; align-items: center; height: 200px; color: #6b7280">
      <div style="text-align: center">
        <a-icon type="exclamation-circle" style="font-size: 48px; margin-bottom: 16px; color: #d1d5db" />
        <p>No invoice data available</p>
      </div>
    </div>

    <!-- Footer -->
    <div style="position: absolute; bottom: 0; left: 0; right: 0; padding: 16px; background: #fff; border-top: 1px solid #e6e6e6">
      <div style="display: flex; justify-content: flex-end; gap: 12px">
        <a-button @click="handleClose" style="border-radius: 6px; background: #26a246; border-color: #26a246; color: #fff">
          Close
        </a-button>
      </div>
    </div>
  </a-drawer>
</template>

<script>
import StatusTag from "../shared-components/StatusTag.vue";
import { formatAmount, formatDate } from "@/utils";
import FinancingAndPaymentsService from "@/api/financing-and-payments.service";

export default {
  name: "InvoiceDisbursementDetailsDrawer",
  components: {
    StatusTag,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    invoiceData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      disbursementHistory: [],
      disbursementLoading: false,
      disbursementColumns: [
        {
          title: "Date Sent",
          dataIndex: "createdAt",
          key: "createdAt",
          width: 150,
          scopedSlots: { customRender: "dateSent" },
        },
        {
          title: "Date Completed",
          dataIndex: "completedAt",
          key: "completedAt",
          width: 150,
          scopedSlots: { customRender: "dateCompleted" },
        },
        {
          title: "Amount",
          dataIndex: "amount",
          key: "amount",
          width: 120,
          scopedSlots: { customRender: "amount" },
        },
        {
          title: "Status",
          dataIndex: "status",
          key: "status",
          width: 120,
          scopedSlots: { customRender: "status" },
        },
      ],
    };
  },
  watch: {
    visible(newVal) {
      if (newVal && this.invoiceData) {
        this.fetchDisbursementHistory();
      }
    },
    invoiceData: {
      handler(newVal) {
        if (newVal && this.visible) {
          this.fetchDisbursementHistory();
        }
      },
      immediate: true,
    },
  },
  methods: {
    formatAmount,
    formatDate,
    async fetchDisbursementHistory() {
      if (!this.invoiceData?.id) return;
      
      this.disbursementLoading = true;
      
      try {
        const response = await FinancingAndPaymentsService.getDisbursements({
          invoiceId: this.invoiceData.id,
          pageSize: 100 // Get all disbursements for this invoice
        });
        
        if (response.data?.code === 200) {
          const data = response.data.data;
          this.disbursementHistory = (data.results || []).map((item) => ({
            id: item.id,
            amount: item.amount,
            status: item.status,
            createdAt: item.createdAt,
            completedAt: item.completedAt,
          }));
        } else {
          this.disbursementHistory = [];
          this.$message.warning("No disbursement history found");
        }
      } catch (error) {
        console.error("Error fetching disbursements:", error);
        this.disbursementHistory = [];
        this.$message.error("Failed to fetch disbursement history");
      } finally {
        this.disbursementLoading = false;
      }
    },
    handleClose() {
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.ant-drawer-body {
  padding: 0;
  position: relative;
  padding-bottom: 80px;
}
</style>
